import React, { Fragment, useMemo } from 'react';
import { isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DeleteProgressAction } from './DeleteProgressAction';
import { ActionSection, ImageShadow, Layout, MediaTypeOverlay, MetaInformationContainer, Overlay } from './ImageOverlay.styles';
import { MediaDuration } from './MediaDuration';
import { SubtitleLabel } from './MediaSubtitles';
import { useA11yOverlayDelay } from './use-a11y-overlay-delay';
import { useVideoProgress } from './use-video-progress';
import { ProgressContainer, VideoProgress } from './VideoProgress';
import { WatchLaterAction } from './WatchLaterAction';
import { MediaTypeIcon } from '../../../../../play-core/icons/MediaTypeIcon';
import { useVideoConfig } from '../../../config/VideoAppContext';
import { useHydrated } from '../../../hooks/use-hydrated';
import { usePlayableAtLocation } from '../../../hooks/use-playable-at-location';
import { useWatchLaterState } from '../../../providers/DataProvider/DataContext';
import { useDeviceState } from '../../../providers/DeviceProvider/DeviceContext';
import { useMediaTeaserConfig } from '../../../providers/UiConfigProvider/UiConfigContext';
import { IconWrapper } from '../../IconWrapper/IconWrapper';
import { getLabel, getLabelMap } from '../../InfoLabel/label-helper';
import { TeaserLabel } from '../../TeaserLabel/TeaserLabel';
import { YouthProtectionSign } from '../../YouthProtection/YouthProtectionSign';
import { AbroadOverlay } from '../TeaserOverlayInfo/AbroadOverlay';
import { ExpiredOverlay } from '../TeaserOverlayInfo/ExpiredOverlay';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MediaImageOverlay = ({
  aspectRatio,
  isHovering,
  isFocused,
  media,
  labelOrder = [],
  hasHoverShadow = true,
  horizontalOnMobile = false,
  showWatchLaterAction = true,
  showWatchLaterUndo = false,
  showDeleteProgressAction = false
}) => {
  const {
    t
  } = useTranslation();
  const {
    displayAgeRating
  } = useMediaTeaserConfig();
  const {
    personalizationSupported
  } = useVideoConfig();
  const {
    videoUrns: watchLaterUrns
  } = useWatchLaterState();
  const isHydrated = useHydrated();
  const {
    isTouchDevice
  } = useDeviceState();
  const videoProgress = useVideoProgress(media);
  const isWatchLaterAdded = watchLaterUrns.includes(media.urn);
  const label = useMemo(() => isHydrated ? getLabel(labelOrder, getLabelMap(t, media)) : undefined, [isHydrated, labelOrder, media, t]);
  const {
    delayedIsHovered,
    delayedIsFocused
  } = useA11yOverlayDelay(isFocused, isHovering);
  const subtitlesAvailable = media?.subtitlesAvailable;
  const isPlayableAtLocation = usePlayableAtLocation({
    playableAbroad: media?.playableAbroad ?? true
  });
  const isMediaExpired = !!media?.validTo && isAfter(new Date(), new Date(media?.validTo));
  const showWatchLater = media && showWatchLaterAction && (isTouchDevice || isWatchLaterAdded || delayedIsFocused || delayedIsHovered);
  const showDeleteProgress = media && showDeleteProgressAction && (isTouchDevice || delayedIsFocused || delayedIsHovered);
  const showMediaType = media && (isHovering || isFocused) && isPlayableAtLocation && !isMediaExpired;
  const showHoverSection = personalizationSupported && (showWatchLater || showDeleteProgress || showMediaType);
  const showImageShadow = (isFocused || isHovering || isTouchDevice) && hasHoverShadow && isPlayableAtLocation && !isMediaExpired;
  const showProgress = videoProgress > 0;
  const showDuration = media?.duration && media?.duration > 0 && media.type !== 'SCHEDULED_LIVESTREAM';
  const showMetaInformation = media && (showDuration || subtitlesAvailable) && isPlayableAtLocation && !isMediaExpired;
  const showYouthProtection = media?.youthProtectionColor && displayAgeRating;
  const overlayVisible = label || showWatchLater || showDeleteProgress || showImageShadow || showMediaType || showProgress || showDuration || subtitlesAvailable || showYouthProtection || isMediaExpired || !isPlayableAtLocation;
  return /*#__PURE__*/_jsx(Fragment, {
    children: overlayVisible && /*#__PURE__*/_jsxs(Layout, {
      aspectRatio: aspectRatio,
      horizontalOnMobile: horizontalOnMobile,
      children: [showImageShadow && /*#__PURE__*/_jsx(ImageShadow, {}), !isPlayableAtLocation && /*#__PURE__*/_jsx(AbroadOverlay, {}), isMediaExpired && isPlayableAtLocation && /*#__PURE__*/_jsx(ExpiredOverlay, {}), /*#__PURE__*/_jsxs(Overlay, {
        children: [/*#__PURE__*/_jsx(TeaserLabel, {
          label: label
        }), showHoverSection && /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsxs(ActionSection, {
            children: [media?.id && showWatchLater && /*#__PURE__*/_jsx(WatchLaterAction, {
              isAdded: isWatchLaterAdded,
              isHovering: isHovering,
              mediaUrn: media.urn,
              showUndo: showWatchLaterUndo
            }), media && showDeleteProgress && /*#__PURE__*/_jsx(DeleteProgressAction, {
              isHovering: isHovering,
              mediaId: media.id
            })]
          }), showMediaType && media?.mediaType && /*#__PURE__*/_jsx(MediaTypeOverlay, {
            children: /*#__PURE__*/_jsx(IconWrapper, {
              size: 60,
              icon: /*#__PURE__*/_jsx(MediaTypeIcon, {
                mediaType: media.mediaType,
                isHovering: false,
                shadow: true
              })
            })
          })]
        }), showMetaInformation && /*#__PURE__*/_jsxs(MetaInformationContainer, {
          children: [showYouthProtection && /*#__PURE__*/_jsx(YouthProtectionSign, {
            youthProtectionColor: media?.youthProtectionColor
          }), subtitlesAvailable && isHydrated && /*#__PURE__*/_jsx(SubtitleLabel, {}), showDuration && media && /*#__PURE__*/_jsx(MediaDuration, {
            media: media
          })]
        }), showProgress && isHydrated && /*#__PURE__*/_jsx(ProgressContainer, {
          children: /*#__PURE__*/_jsx(VideoProgress, {
            percentage: videoProgress
          })
        })]
      })]
    })
  });
};